import { useQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { usePathname } from "next/navigation";
import log from "logbeacon";
import { rootStore } from "../store";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { transformContentsToMarketSummaryItems } from "@/src/services/marketSummaryService";
import type { MarketSummaryItemData } from "@/src/components/Home/MarketSummary/types";
import { homeQueryKeys } from "@/src/services/homeQueryKeys";
import {
  type StockType,
  getStockTypeFromPathname,
  DEFAULT_STOCK_TYPE,
} from "@/src/utils/stockType";

interface UseMarketSummaryOptions {
  /** 是否启用查询 */
  enabled?: boolean;
  /** 缓存时间，默认5分钟 */
  staleTime?: number;
  /** 初始数据，用于 SSR */
  initialData?: MarketSummaryItemData[];
  /** 初始数据更新时间 */
  initialDataUpdatedAt?: number;
  /** 语言代码 */
  language?: string;
  /** 页面类型：stock 或 crypto */
  type?: "stock" | "crypto";
  /** 股票市场类型（US | HK），当 type 为 "stock" 时使用
   * @note 优先使用传入的值，如果没有传入则从路由 pathname 解析
   */
  stockType?: StockType;
}

/**
 * 获取市场摘要数据的 Hook
 *
 * 通过两步 API 调用获取数据：
 * 1. 调用 recommend 接口获取对应 scope 的 ids（STOCK_MARKET_SUMMARY 或 TOKEN_MARKET_SUMMARY）
 * 2. 调用 contents 接口获取详细内容
 */
export const useMarketSummary = (options?: UseMarketSummaryOptions) => {
  const { openDataApi } = useOspApiClientProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const pathname = usePathname();
  const {
    enabled = true,
    stockType: propsStockType,
    language = "en-US",
    type = "stock",
  } = options || {};
  // 优先使用传入的 stockType，fallback 到从 pathname 解析，最后使用默认值
  const stockType: StockType =
    propsStockType ?? getStockTypeFromPathname(pathname) ?? DEFAULT_STOCK_TYPE;

  const { data, isPending, isError, error } = useQuery({
    queryKey: homeQueryKeys.marketSummary(
      language,
      type,
      !!isLoggedIn,
      stockType,
    ),
    enabled: !!openDataApi && enabled,
    queryFn: async () => {
      if (!openDataApi?.recommend || !openDataApi?.contents) {
        throw new Error("OpenDataApi recommend or contents is not available");
      }

      try {
        // 第一步：获取推荐 IDs
        // 根据页面类型选择对应的 scope
        const scopeType =
          type === "crypto"
            ? "TOKEN_MARKET_SUMMARY"
            : stockType === "HK"
              ? "HK_STOCK_MARKET_SUMMARY"
              : "STOCK_MARKET_SUMMARY";
        // 注意：需要将 scopes 对象展开为 scopes[KEY]=VALUE 格式
        // 由于 HttpClient 不支持对象展开，我们需要手动构建查询参数
        const scopes = { [scopeType]: 6 };
        const scopesQueryParams: Record<string, string | number> = {};
        Object.keys(scopes).forEach((key) => {
          scopesQueryParams[`scopes[${key}]`] =
            scopes[key as keyof typeof scopes];
        });

        const recommendResponse = await openDataApi.recommend.getRecommend(
          scopesQueryParams as unknown as {
            scopes: Record<string, number>;
          },
        );

        // 从响应中提取对应 scope 的 ids
        const recommendData = recommendResponse?.data?.data;
        const marketSummaryItems = recommendData?.[scopeType] || [];
        const ids = marketSummaryItems
          .map((item) => item.id)
          .filter((id): id is string => !!id);

        if (ids.length === 0) {
          return [];
        }

        // 第二步：根据 IDs 获取详细内容
        // 注意：接口已改为扁平化查询参数格式
        // 需要手动构建查询参数，特别是 enrich 相关的点号分隔参数
        const contentsQueryParams = {
          offset: 0,
          limit: ids.length,
          language,
          ids, // ids 作为数组传递，HttpClient 会自动展开为多个 ids=... 参数
          "enrich.enable": true,
          "enrich.with_symbol.enable": true,
          "enrich.with_symbol.with_price": true,
        };

        const contentsResponse =
          await openDataApi.contents.getContents(contentsQueryParams);

        const contents = contentsResponse?.data?.data?.rows || [];
        return transformContentsToMarketSummaryItems(contents, stockType);
      } catch (error) {
        log.error("[useMarketSummary] Error fetching market summary:", error);
        throw error;
      }
    },
  });

  return {
    data: data || [],
    isLoading: isPending,
    isError,
    error,
  };
};
